import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Alert } from '../../../components/ui/Alert'
import { Modal } from '../../../components/ui/Modal'
import { inputClass, selectClass } from '../../../components/ui/FormField'
import { api } from '../../../api/axios'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { Contas } from '../components/Contas'
import { Anuidades } from '../components/Anuidades'
import { Bancos } from '../components/Bancos'
import { DataBR } from '../../../components/ui/DataBR'
import { dataLocalISO } from '../../../lib/dataLocal'

interface Despesa {
  iddespesa: number
  idetapa: number
  descricao: string
  categoria: string | null
  valor: string
  data_despesa: string
  observacao: string | null
}

interface PdfResult { arquivo: string; url: string; whatsapp: string }

const CATEGORIAS = ['Alimentacao', 'Premiacao', 'Aluguel', 'Material', 'Transporte', 'Limpeza', 'Manutencao', 'Outras']

// Modal de visualizacao do PDF (mesmo padrao dos outros relatorios)
function PdfPreviewModal({ result, titulo, onClose }: {
  result: PdfResult; titulo: string; onClose: () => void
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    let active = true; let createdUrl: string | null = null
    setLoading(true); setErro(null)
    api.get(`/relatorios/arquivo/${result.arquivo}`, { responseType: 'blob' })
      .then(resp => {
        if (!active) return
        createdUrl = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }))
        setBlobUrl(createdUrl)
      })
      .catch(err => active && setErro(err?.response?.data?.error || err?.message || 'Erro'))
      .finally(() => active && setLoading(false))
    return () => { active = false; if (createdUrl) URL.revokeObjectURL(createdUrl) }
  }, [result.arquivo])

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex flex-col" onClick={onClose}>
      <div className="flex-1 flex flex-col bg-compumaq-pretoSuave m-4 rounded-xl overflow-hidden shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <div className="bg-compumaq-pretoSuave border-b px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold text-compumaq-texto">{titulo}</h3>
          <div className="flex gap-2">
            <button onClick={() => iframeRef.current?.contentWindow?.print()} disabled={!blobUrl}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50">
              Imprimir
            </button>
            <button onClick={() => {
              if (!blobUrl) return
              const a = document.createElement('a'); a.href = blobUrl; a.download = result.arquivo
              document.body.appendChild(a); a.click(); document.body.removeChild(a)
            }} disabled={!blobUrl}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
              Baixar PDF
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-compumaq-pretoSuave text-compumaq-texto rounded-lg text-sm hover:bg-compumaq-pretoSuave">
              Fechar
            </button>
          </div>
        </div>
        <div className="flex-1 bg-compumaq-pretoSuave relative">
          {loading && <div className="absolute inset-0 flex items-center justify-center"><Spinner size="lg" /></div>}
          {erro && <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md">
              <p className="font-semibold text-red-800 mb-2">Erro</p><p className="text-sm text-red-700">{erro}</p>
            </div>
          </div>}
          {blobUrl && !erro && <iframe ref={iframeRef} src={blobUrl} className="w-full h-full border-0" title={titulo} />}
        </div>
      </div>
    </div>
  )
}

type Tab = 'despesas' | 'fechamento' | 'pagar' | 'receber' | 'anuidades' | 'bancos'

export function FinanceiroPage() {
  const [tab, setTab] = useState<Tab>('despesas')
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [selCampeonato, setSelCampeonato] = useState<number | ''>('')
  const [selNumeroEtapa, setSelNumeroEtapa] = useState<number | ''>('')
  const [pdf, setPdf] = useState<PdfResult | null>(null)
  const [gerando, setGerando] = useState(false)
  const qc = useQueryClient()

  // Form despesa
  const [editing, setEditing] = useState<Despesa | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<Partial<Despesa> & { valorNum?: number }>({})

  const { data: campsData } = useCampeonatos({ limit: 200 })

  const { data: etapasData } = useQuery({
    queryKey: ['etapas-pub-fin', selCampeonato],
    queryFn: async () => {
      if (!selCampeonato) return []
      const r = await api.get<{ data: Array<{ idetapa: number; titulo: string; titulo_reduzido: string | null; numero_etapa: number }> }>(
        '/publico/etapas', { params: { idcampeonato: selCampeonato } })
      return r.data.data
    },
    enabled: !!selCampeonato,
  })
  const etapas = etapasData ?? []
  // Numeros de etapa distintos (agrupado — cada modalidade tem sua propria
  // linha de etapa, entao o filtro nao deve listar uma por modalidade, so o
  // numero da etapa em si: 1a, 2a, 3a...)
  const numerosEtapa = Array.from(
    new Map(
      etapas.map(e => [e.numero_etapa, e.titulo_reduzido || e.titulo || `${e.numero_etapa}a Etapa`]),
    ).entries(),
  ).sort((a, b) => a[0] - b[0])

  const { data: despesasData, isLoading: loadingDesp } = useQuery({
    queryKey: ['despesas', selNumeroEtapa, selCampeonato],
    queryFn: async () => {
      const params: any = {}
      if (selNumeroEtapa && selCampeonato) { params.idcampeonato = selCampeonato; params.numero_etapa = selNumeroEtapa }
      else if (selCampeonato) params.idcampeonato = selCampeonato
      const r = await api.get<{ data: Despesa[] }>('/financeiro/despesas', { params })
      return r.data.data
    },
    enabled: !!selNumeroEtapa || !!selCampeonato,
  })
  const despesas = despesasData ?? []
  const totalDespesas = despesas.reduce((s, d) => s + Number(d.valor), 0)

  function openCreate() {
    if (!selNumeroEtapa) { setAlert({ type: 'error', message: 'Selecione uma etapa primeiro' }); return }
    setEditing(null); setForm({ categoria: 'Outras', data_despesa: dataLocalISO() })
    setShowForm(true)
  }
  function openEdit(d: Despesa) {
    setEditing(d); setForm({ ...d, valorNum: Number(d.valor) }); setShowForm(true)
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: any = {
        descricao: form.descricao,
        categoria: form.categoria,
        valor: form.valorNum ?? Number(form.valor),
        data_despesa: form.data_despesa,
        observacao: form.observacao,
      }
      if (editing) {
        return api.put(`/financeiro/despesas/${editing.iddespesa}`, payload).then(r => r.data.data)
      }
      payload.idcampeonato = selCampeonato
      payload.numero_etapa = selNumeroEtapa
      return api.post('/financeiro/despesas', payload).then(r => r.data.data)
    },
    onSuccess: () => {
      setAlert({ type: 'success', message: editing ? 'Despesa atualizada!' : 'Despesa cadastrada!' })
      setShowForm(false)
      qc.invalidateQueries({ queryKey: ['despesas'] })
    },
    onError: (err: any) => setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao salvar' }),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.delete(`/financeiro/despesas/${id}`).then(r => r.data.data),
    onSuccess: () => {
      setAlert({ type: 'success', message: 'Despesa excluida!' })
      qc.invalidateQueries({ queryKey: ['despesas'] })
    },
    onError: (err: any) => setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao excluir' }),
  })

  async function gerarFechamento() {
    if (!selNumeroEtapa || !selCampeonato) { setAlert({ type: 'error', message: 'Selecione uma etapa' }); return }
    setAlert(null); setPdf(null); setGerando(true)
    try {
      const { data } = await api.get<{ data: PdfResult }>('/financeiro/fechamento', { params: { idcampeonato: selCampeonato, numero_etapa: selNumeroEtapa } })
      setPdf(data.data)
    } catch (err: any) {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao gerar fechamento' })
    } finally { setGerando(false) }
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Financeiro" description="Despesas da etapa, fechamento, contas a pagar e a receber" />
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Tabs */}
      <div className="flex gap-1 bg-compumaq-pretoSuave rounded-lg p-1 flex-wrap">
        <button onClick={() => setTab('despesas')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'despesas' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}>
          Despesas
        </button>
        <button onClick={() => setTab('fechamento')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'fechamento' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}>
          Fechamento Financeiro
        </button>
        <button onClick={() => setTab('pagar')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'pagar' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}>
          Contas a Pagar
        </button>
        <button onClick={() => setTab('receber')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'receber' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}>
          Contas a Receber
        </button>
        <button onClick={() => setTab('anuidades')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'anuidades' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}>
          Anuidades e Joia
        </button>
        <button onClick={() => setTab('bancos')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'bancos' ? 'bg-compumaq-pretoSuave shadow text-compumaq-texto' : 'text-compumaq-textoSuave hover:text-compumaq-texto'}`}>
          Contas Bancarias e Integracoes
        </button>
      </div>

      {tab === 'bancos' && <Bancos onAlert={setAlert} />}

      {tab === 'anuidades' && <Anuidades onAlert={setAlert} onVerContas={() => setTab('receber')} />}

      {(tab === 'pagar' || tab === 'receber') && <Contas key={tab} tipo={tab} onAlert={setAlert} />}

      {/* Filtros compartilhados (despesas e fechamento) */}
      {(tab === 'despesas' || tab === 'fechamento') && (
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
            <select className={selectClass} value={selCampeonato}
              onChange={e => { setSelCampeonato(e.target.value ? parseInt(e.target.value) : ''); setSelNumeroEtapa('') }}>
              <option value="">Selecione...</option>
              {campsData?.data?.map(c => (
                <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
            <select className={selectClass} value={selNumeroEtapa}
              onChange={e => setSelNumeroEtapa(e.target.value ? parseInt(e.target.value) : '')}
              disabled={!selCampeonato}>
              <option value="">{selCampeonato ? 'Selecione...' : '— selecione campeonato —'}</option>
              {numerosEtapa.map(([numero, label]) => (
                <option key={numero} value={numero}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      )}

      {/* DESPESAS */}
      {tab === 'despesas' && (
        <>
          <div className="flex justify-between items-center">
            <div className="text-sm text-compumaq-textoSuave">
              Total de despesas: <span className="font-bold text-red-700">R$ {totalDespesas.toFixed(2).replace('.', ',')}</span>
            </div>
            <button onClick={openCreate} disabled={!selNumeroEtapa}
              className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-semibold hover:bg-compumaq-doradoClaro disabled:opacity-50">
              + Nova Despesa
            </button>
          </div>

          <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
            {loadingDesp ? (
              <div className="p-12 flex justify-center"><Spinner size="lg" /></div>
            ) : !selNumeroEtapa ? (
              <div className="p-12 text-center text-compumaq-textoSuave">Selecione um campeonato e uma etapa para ver as despesas.</div>
            ) : despesas.length === 0 ? (
              <div className="p-12 text-center text-compumaq-textoSuave">Nenhuma despesa lancada para esta etapa.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-compumaq-preto text-xs">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Data</th>
                      <th className="text-left px-3 py-2 font-medium">Categoria</th>
                      <th className="text-left px-3 py-2 font-medium">Descricao</th>
                      <th className="text-right px-3 py-2 font-medium">Valor</th>
                      <th className="text-right px-3 py-2 font-medium">Acoes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-compumaq-dourado/25">
                    {despesas.map(d => (
                      <tr key={d.iddespesa} className="hover:bg-compumaq-preto">
                        <td className="px-3 py-2">{new Date(d.data_despesa + 'T12:00').toLocaleDateString('pt-BR')}</td>
                        <td className="px-3 py-2 text-compumaq-texto">{d.categoria || '—'}</td>
                        <td className="px-3 py-2">{d.descricao}</td>
                        <td className="px-3 py-2 text-right font-semibold text-red-700">
                          R$ {Number(d.valor).toFixed(2).replace('.', ',')}
                        </td>
                        <td className="px-3 py-2 text-right space-x-3">
                          <button onClick={() => openEdit(d)}
                            className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium">
                            Editar
                          </button>
                          <button onClick={() => {
                            if (window.confirm(`Excluir esta despesa?\n\n${d.descricao}\nR$ ${Number(d.valor).toFixed(2)}`))
                              deleteMut.mutate(d.iddespesa)
                          }}
                            className="text-xs text-red-600 hover:text-red-800 font-medium">
                            Excluir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* FECHAMENTO */}
      {tab === 'fechamento' && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-4">
          <h2 className="font-semibold text-compumaq-texto">Relatorio de Fechamento Financeiro</h2>
          <p className="text-sm text-compumaq-textoSuave">
            Gera o PDF com inscricoes pagas por forma de pagamento, isentas, despesas, saldo liquido e estatisticas
            (total de atiradores, reinscricoes).
          </p>
          <button onClick={gerarFechamento} disabled={!selNumeroEtapa || gerando}
            className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 flex items-center gap-2">
            {gerando ? <Spinner size="sm" /> : <span>📊</span>}
            {gerando ? 'Gerando...' : 'Gerar Fechamento Financeiro'}
          </button>
          {pdf && <PdfPreviewModal result={pdf} titulo="Fechamento Financeiro" onClose={() => setPdf(null)} />}
        </div>
      )}

      {/* Modal Nova/Editar Despesa */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Editar Despesa' : 'Nova Despesa'} size="md">
        <form onSubmit={e => { e.preventDefault(); saveMut.mutate() }} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Categoria</label>
              <select className={selectClass} value={form.categoria ?? ''}
                onChange={e => setForm({ ...form, categoria: e.target.value })}>
                <option value="">— selecione —</option>
                {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Data *</label>
              <DataBR className={inputClass} value={form.data_despesa ?? ''}
                onChange={e => setForm({ ...form, data_despesa: e.target.value })} required />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Descricao *</label>
              <input className={inputClass} value={form.descricao ?? ''}
                onChange={e => setForm({ ...form, descricao: e.target.value })} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Valor (R$) *</label>
              <input type="number" step="0.01" min="0" className={inputClass}
                value={form.valorNum ?? form.valor ?? ''}
                onChange={e => setForm({ ...form, valorNum: parseFloat(e.target.value) || 0 })} required />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Observacao</label>
              <textarea className={inputClass} rows={2} value={form.observacao ?? ''}
                onChange={e => setForm({ ...form, observacao: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-compumaq-dourado/25 rounded-lg text-sm">
              Cancelar
            </button>
            <button type="submit" disabled={saveMut.isPending}
              className="flex-1 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-semibold hover:bg-compumaq-doradoClaro disabled:opacity-50">
              {saveMut.isPending ? 'Salvando...' : editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
